import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl, API_ENDPOINTS } from '../../config/api';
import RoomManagement from './RoomManagement';
import PreBookedRooms from './PreBookedRooms';
import StaffManagement from './StaffManagement';
import AdminGuestHistory from './AdminGuestHistory';
import Reports from './Reports';
import AdminMaintenance from './Maintenance';
import AdminHousekeeping from './AdminHousekeeping';
import EmailManagementDashboard from '../EmailManagementDashboard';
import TallyIntegration from './TallyIntegration';
import AdminProfileDropdown from './AdminProfileDropdown';
import RoomAvailability from '../booking/RoomAvailability';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false); // For mobile only
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On mobile, sidebar should be closed by default
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardStats();
    }
  }, [activeTab]);

  // Set active tab based on current location
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/maintenance')) {
      setActiveTab('maintenance');
    } else if (path.includes('/housekeeping')) {
      setActiveTab('housekeeping');
    } else if (path.includes('/prebooked-rooms')) {
      setActiveTab('prebooked-rooms');
    } else if (path.includes('/room-availability')) {
      setActiveTab('room-availability');
    } else if (path.includes('/rooms')) {
      setActiveTab('rooms');
    } else if (path.includes('/staff')) {
      setActiveTab('staff');
    } else if (path.includes('/guest-history')) {
      setActiveTab('guest-history');
    } else if (path.includes('/reports')) {
      setActiveTab('reports');
    } else if (path.includes('/email-management')) {
      setActiveTab('email-management');
    } else if (path.includes('/tally-integration')) {
      setActiveTab('tally-integration');
    } else {
      setActiveTab('dashboard');
    }
  }, [location.pathname]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_DASHBOARD));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setDashboardStats(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', description: 'Overview' },
    { id: 'rooms', label: 'Rooms', icon: '🏠', description: 'Manage rooms' },
    { id: 'room-availability', label: 'Room Availability', icon: '🔍', description: 'Check room status' },
    { id: 'prebooked-rooms', label: 'Pre-booked', icon: '📅', description: 'Future bookings' },
    { id: 'staff', label: 'Staff', icon: '👥', description: 'Staff management' },
    { id: 'guest-history', label: 'Guests', icon: '👤', description: 'Guest records' },
    { id: 'reports', label: 'Reports', icon: '📈', description: 'Analytics' },
    { id: 'maintenance', label: 'Maintenance', icon: '🔧', description: 'Room issues' },
    { id: 'housekeeping', label: 'Housekeeping', icon: '🧹', description: 'Cleaning tasks' },
    { id: 'email-management', label: 'Email', icon: '📧', description: 'Communications' },
    { id: 'tally-integration', label: 'Tally', icon: '🧮', description: 'Accounting' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Mobile Header */}
      <header className="bg-white shadow md:hidden fixed top-0 left-0 right-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 mr-3"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="admin-logo">
                <img 
                  src="/WhatsApp Image 2025-09-17 at 20.15.10_e9513b22.jpg" 
                  alt="SV Royal Luxury Rooms Logo" 
                  className="admin-logo-image"
                />
              </div>
            </div>
            <AdminProfileDropdown user={user} />
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="bg-white shadow-sm hidden md:block fixed top-0 left-0 right-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center">
              <div className="admin-logo">
                <img 
                  src="/WhatsApp Image 2025-09-17 at 20.15.10_e9513b22.jpg" 
                  alt="SV Royal Luxury Rooms Logo" 
                  className="admin-logo-image"
                />
              </div>
            </div>
            <AdminProfileDropdown user={user} />
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && isMobile && (
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
            style={{ top: '4rem' }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          sidebar w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out 
          ${isMobile ? (sidebarOpen ? 'open' : '') : 'open'}
        `}>
          <div className="flex items-center justify-between p-4 border-b md:hidden">
            <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="p-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.id === 'dashboard' ? '/admin' : `/admin/${item.id}`}
                onClick={() => {
                  setActiveTab(item.id);
                  if (isMobile) setSidebarOpen(false);
                }}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === item.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="mr-3 text-base">{item.icon}</span>
                <div className="font-medium">{item.label}</div>
              </Link>
            ))}
          </nav>

        </div>

        {/* Main Content */}
        <div className={`flex-1 min-w-0 flex flex-col transition-all duration-300 ease-in-out ${
          !isMobile ? 'main-content-with-sidebar' : ''
        } pt-16`}>
          <div className="flex-1 transition-all duration-300 ease-in-out">
            <div className="w-full px-4 sm:px-6 lg:px-8 py-4 md:py-8 transition-all duration-300 ease-in-out max-w-7xl mx-auto">
            {activeTab === 'dashboard' && <DashboardOverview stats={dashboardStats} loading={loading} setActiveTab={setActiveTab} />}
            {activeTab === 'rooms' && <RoomManagement />}
            {activeTab === 'room-availability' && <RoomAvailability />}
            {activeTab === 'prebooked-rooms' && <PreBookedRooms />}
            {activeTab === 'staff' && <StaffManagement />}
            {activeTab === 'guest-history' && <AdminGuestHistory />}
            {activeTab === 'reports' && <Reports />}
            {activeTab === 'maintenance' && <AdminMaintenance />}
            {activeTab === 'housekeeping' && <AdminHousekeeping />}
            {activeTab === 'email-management' && <EmailManagementDashboard />}
            {activeTab === 'tally-integration' && <TallyIntegration />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Dashboard Overview Component
const DashboardOverview = ({ stats, loading, setActiveTab }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivity();
  }, []);



  const fetchRecentActivity = async () => {
    try {
      setActivityLoading(true);
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_ACTIVITY_LOGS));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecentActivity(data.data.recent_activity);
        }
      }
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }



  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <div className="flex gap-2">
          <button
            onClick={() => window.location.reload()}
            className="w-8 h-8 bg-blue-600 text-white rounded-full text-xs hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors flex items-center justify-center"
            title="Refresh"
          >
            🔄
          </button>
          <button
            onClick={() => window.location.href = '/admin/reports'}
            className="w-8 h-8 bg-green-600 text-white rounded-full text-xs hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors flex items-center justify-center"
            title="Reports"
          >
            📊
          </button>
        </div>
      </div>
      
      {/* Quick Stats - Top Row */}
      <div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Quick Stats</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                <span className="text-xl sm:text-2xl">💰</span>
              </div>
              <div className="ml-2 sm:ml-3">
                <div className="text-lg sm:text-2xl font-bold text-blue-600">
                  {formatCurrency(stats?.revenue?.today)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Today's Revenue</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                <span className="text-xl sm:text-2xl">🏢</span>
              </div>
              <div className="ml-2 sm:ml-3">
                <div className="text-lg sm:text-2xl font-bold text-green-600">
                  {stats?.occupancy?.current?.occupancy_rate || 0}%
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Occupancy Rate</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                <span className="text-xl sm:text-2xl">📅</span>
              </div>
              <div className="ml-2 sm:ml-3">
                <div className="text-lg sm:text-2xl font-bold text-purple-600">
                  {formatCurrency(stats?.revenue?.month)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Monthly Revenue</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-yellow-100 rounded-lg">
                <span className="text-xl sm:text-2xl">👥</span>
              </div>
              <div className="ml-2 sm:ml-3">
                <div className="text-lg sm:text-2xl font-bold text-yellow-600">
                  {formatNumber(stats?.guests?.total)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Total Guests</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats - Middle Row */}
      <div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Additional Stats</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-indigo-600 mb-1">
                {formatNumber(stats?.bookings?.today || 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">New bookings today</div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600 mb-1">
                {stats?.rooms?.available || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Ready for booking</div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-orange-600 mb-1">
                {stats?.bookings?.avg_stay_days || 0} days
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Typical guest duration</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Recent Activity and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Activity */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Recent Activity</h3>
          <div className="space-y-2 sm:space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center text-xs sm:text-sm text-gray-600">
                  <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-blue-500 rounded-full mr-2 sm:mr-3"></span>
                  <span className="flex-1">{activity.description || 'Activity logged'}</span>
                  <span className="text-xs text-gray-400">{formatDate(activity.created_at)}</span>
                </div>
              ))
            ) : (
              <div className="text-xs sm:text-sm text-gray-500 italic">No recent activity</div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 gap-2 sm:gap-3">
            <button
              onClick={() => window.location.href = '/admin/rooms'}
              className="flex items-center p-2.5 sm:p-3 text-left bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
            >
              <span className="text-lg sm:text-xl mr-2 sm:mr-3">🏠</span>
              <span className="font-medium text-blue-800 text-sm sm:text-base">Add New Room</span>
            </button>
            
            <button
              onClick={() => setActiveTab('room-availability')}
              className="flex items-center p-2.5 sm:p-3 text-left bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
            >
              <span className="text-lg sm:text-xl mr-2 sm:mr-3">🔍</span>
              <span className="font-medium text-indigo-800 text-sm sm:text-base">Check Room Availability</span>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/staff'}
              className="flex items-center p-2.5 sm:p-3 text-left bg-green-50 hover:bg-green-100 rounded-md transition-colors"
            >
              <span className="text-lg sm:text-xl mr-2 sm:mr-3">👥</span>
              <span className="font-medium text-green-800 text-sm sm:text-base">Add Staff Member</span>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/reports'}
              className="flex items-center p-2.5 sm:p-3 text-left bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
            >
              <span className="text-lg sm:text-xl mr-2 sm:mr-3">📊</span>
              <span className="font-medium text-purple-800 text-sm sm:text-base">Generate Report</span>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/maintenance'}
              className="flex items-center p-2.5 sm:p-3 text-left bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
            >
              <span className="text-lg sm:text-xl mr-2 sm:mr-3">🔧</span>
              <span className="font-medium text-orange-800 text-sm sm:text-base">Report Issue</span>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/tally-integration'}
              className="flex items-center p-2.5 sm:p-3 text-left bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
            >
              <span className="text-lg sm:text-xl mr-2 sm:mr-3">🧮</span>
              <span className="font-medium text-indigo-800 text-sm sm:text-base">Export to Tally</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
